import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';
import api from '../services/api';

/**
 * WellnessTrendSparkline — a subtle 7-day Wellness IQ trend under the dashboard
 * hero. Reads /api/wellness/iq/trend (daily snapshots written each time the IQ
 * is computed). Honest "building" state until ≥2 days exist.
 */
const W = 600;
const H = 90;

const scoreColor = (s) => s >= 85 ? '#a3e635' : s >= 65 ? '#CCFF00' : s >= 45 ? '#facc15' : s >= 25 ? '#fb923c' : '#f87171';

const WellnessTrendSparkline = () => {
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    const load = () => api.get('/wellness/iq/trend?days=7').then(r => setTrend(r.data)).catch(() => {}).finally(() => setLoading(false));
    load();
    const onRefresh = () => load();
    window.addEventListener('wellness:refresh', onRefresh);
    return () => window.removeEventListener('wellness:refresh', onRefresh);
  }, []);

  if (loading) return <div className="hpme-card p-5 h-[140px] animate-pulse" data-testid="wellness-trend-loading" />;

  const points = trend?.points || [];
  const color = scoreColor(trend?.current ?? 0);

  // Building state — not enough history yet.
  if (points.length < 2) {
    return (
      <div className="hpme-card p-5" data-testid="wellness-trend-building">
        <div className="hpme-card-corner" />
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-secondary font-bold">7-DAY TREND</p>
            <p className="text-sm text-zinc-300 mt-1">Your trend starts now.</p>
          </div>
          <TrendingUp className="w-5 h-5 text-zinc-600" />
        </div>
        <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
          Check in daily and your Wellness IQ line builds here — so you can see whether you're trending up, not just today's number.
        </p>
      </div>
    );
  }

  const values = points.map(p => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = (max - min) || 1;
  const pad = 8;
  const innerH = H - pad * 2;
  const step = W / (values.length - 1);
  const xy = values.map((v, i) => [i * step, pad + innerH - ((v - min) / range) * innerH]);
  const linePts = xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPts = `0,${H} ${linePts} ${W},${H}`;
  const [lastX, lastY] = xy[xy.length - 1];

  const delta = trend?.delta;
  const DeltaIcon = delta == null || delta === 0 ? Minus : delta > 0 ? TrendingUp : TrendingDown;
  const deltaColor = delta == null || delta === 0 ? '#a1a1aa' : delta > 0 ? '#a3e635' : '#fb923c';
  const gid = 'wellnessTrendGrad';

  return (
    <div className="hpme-card p-5 relative overflow-hidden" data-testid="wellness-trend-sparkline">
      <div className="hpme-card-corner" />
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-secondary font-bold">7-DAY WELLNESS IQ TREND</p>
          <button
            onClick={() => setShowInfo(s => !s)}
            className="text-zinc-600 hover:text-zinc-300 transition-colors"
            data-testid="wellness-trend-info-btn"
            aria-label="What is this?"
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-secondary font-bold text-2xl leading-none" style={{ color }} data-testid="wellness-trend-current">{trend.current}</span>
          {delta != null && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-sm" style={{ color: deltaColor, backgroundColor: `${deltaColor}1a` }} data-testid="wellness-trend-delta">
              <DeltaIcon className="w-3 h-3" />{delta > 0 ? '+' : ''}{delta}
            </span>
          )}
        </div>
      </div>

      {showInfo && (
        <p className="text-xs text-zinc-400 mb-3 leading-relaxed border-l-2 pl-3" style={{ borderColor: color }} data-testid="wellness-trend-explainer">
          Each point is your blended Wellness IQ (Movement, Recovery, Mental, Nutrition) for that day. The line shows direction — the goal isn't a perfect 100, it's a line that drifts upward over weeks.
        </p>
      )}

      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={areaPts} fill={`url(#${gid})`} />
        <polyline points={linePts} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={lastX} cy={lastY} r="4.5" fill={color} />
        <circle cx={lastX} cy={lastY} r="9" fill={color} opacity="0.18" />
      </svg>

      <div className="flex items-center justify-between mt-1 text-[10px] text-zinc-500">
        <span>{points.length} day{points.length === 1 ? '' : 's'} · low {min} · high {max}</span>
        <span style={{ color: deltaColor }}>
          {delta == null || delta === 0 ? 'holding steady' : delta > 0 ? `up ${delta} this week` : `down ${Math.abs(delta)} this week`}
        </span>
      </div>
    </div>
  );
};

export default WellnessTrendSparkline;
